
import { useState } from "react";
import { useWebsiteAnalyzer } from "./useWebsiteAnalyzer";
import { BusinessData } from "@/api/business/types";

interface UseBusinessWebsiteProps {
  businessData: BusinessData;
  updateBusinessData: (key: string, value: string | string[] | number | Record<string, string>) => void;
  updateMultipleFields: (updates: Record<string, string | string[] | number | Record<string, string>>) => void;
}

export const useBusinessWebsite = ({
  businessData,
  updateBusinessData,
  updateMultipleFields
}: UseBusinessWebsiteProps) => {
  // Initialize website input with business data or empty string
  const [websiteInput, setWebsiteInput] = useState(businessData.website || "");
  const [analysisSuccess, setAnalysisSuccess] = useState(false);
  
  // Use the website analyzer hook
  const { 
    isAnalyzing, 
    analysisError, 
    analyzeWebsite: runAnalysis,
  } = useWebsiteAnalyzer({ 
    businessData,
    updateBusinessData,
    updateMultipleFields
  });
  
  // Format website URL to ensure it has https:// prefix
  const formatWebsiteUrl = (url: string): string => {
    if (!url) return "";
    
    // Add https:// prefix if it doesn't already have a protocol
    if (!url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    
    return url;
  };
  
  // Handle website input change
  const handleWebsiteChange = (value: string) => {
    setWebsiteInput(value);
    // Reset success state when user changes input
    setAnalysisSuccess(false);
  };
  
  // Save the website URL with proper formatting
  const saveWebsite = () => {
    if (websiteInput && websiteInput !== businessData.website) {
      const formattedUrl = formatWebsiteUrl(websiteInput);
      updateBusinessData("website", formattedUrl);
      setWebsiteInput(formattedUrl);
    }
  };
  
  // Determine if website can be analyzed (has URL and not on cooldown)
  const canAnalyzeWebsite = () => {
    // Check if we have a website URL
    if (!websiteInput) return false;
    
    // Check if we're on cooldown (last analyzed less than 24 hours ago)
    if (businessData.lastAnalyzedAt) {
      const now = Date.now();
      const cooldownPeriod = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      return (now - businessData.lastAnalyzedAt) > cooldownPeriod;
    }
    
    return true;
  };
  
  // Run website analysis
  const analyzeWebsite = async () => {
    try {
      const result = await runAnalysis();
      if (result !== undefined) {
        setAnalysisSuccess(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error analyzing website:", error);
      return false;
    }
  };
  
  return {
    websiteInput,
    handleWebsiteChange,
    saveWebsite,
    formatWebsiteUrl,
    analyzeWebsite,
    canAnalyzeWebsite,
    isAnalyzing,
    analysisError,
    analysisSuccess,
  };
};
